// The component kernel: Button, fields, Card, StateChip, status, EmptyState, Skeleton.
//
// Three rules hold across every component here.
//
// 1. PRESENTATIONAL ONLY. Nothing fetches data. Everything arrives via props from
//    route-level loaders (bead hf-e2-design-shell-ej8.2).
//
// 2. NO COPY. Components take state IDENTIFIERS and display STRINGS as props. Every
//    user-facing word lives in @herefor/product-language, which this package does not
//    import — that package owns the words, this one owns the pixels. A component with
//    a hardcoded English string is a component the tone rules cannot reach.
//
// 3. ACCESSIBILITY IS THE CONTRACT, not a later audit. Visible focus, 44px targets,
//    real ARIA wiring, and behaviour verified with Testing Library as built.

import {
  forwardRef,
  useId,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
} from "react";

import type { StateTokenKey } from "../tokens";
import { cx } from "./styles";

const kebab = (value: string): string => value.replace(/([a-z0-9])([A-Z])/g, "$1-$2").toLowerCase();

/* ------------------------------------------------------------------ Button */

export type ButtonVariant = "primary" | "secondary" | "danger";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  readonly variant?: ButtonVariant;
  readonly children: ReactNode;
}

const buttonVariantClass: Record<ButtonVariant, string> = {
  primary: cx.buttonPrimary,
  secondary: cx.buttonSecondary,
  danger: cx.buttonDanger,
};

/**
 * A real `<button>`, never a clickable div: keyboard activation, the correct implicit
 * role and disabled semantics all come free and cannot be forgotten.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = "secondary", type = "button", className, children, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      // Defaulting to type="button" matters: an unset type inside a form is
      // "submit", which turns an innocuous button into an accidental submission.
      type={type}
      className={[cx.button, buttonVariantClass[variant], className].filter(Boolean).join(" ")}
      {...rest}
    >
      {children}
    </button>
  );
});

/* ------------------------------------------------------------------- Field */

interface FieldShellProps {
  readonly label: string;
  // `| undefined` is required, not noise: tsconfig sets exactOptionalPropertyTypes,
  // so an omitted prop and an explicitly-undefined prop are different types, and
  // these are forwarded from callers that may pass either.
  readonly hint?: string | undefined;
  readonly error?: string | undefined;
  readonly required?: boolean | undefined;
  readonly children: (ids: {
    controlId: string;
    describedBy: string | undefined;
    invalid: boolean;
  }) => ReactNode;
}

/**
 * Shared label/hint/error wiring.
 *
 * The wiring is centralised because it is the part that gets done inconsistently:
 * every field gets a real `<label for>`, and hint and error text are joined into
 * `aria-describedby` so a screen reader announces them with the control rather than
 * stranding them as unrelated text.
 */
function FieldShell({ label, hint, error, required, children }: FieldShellProps) {
  const controlId = useId();
  const hintId = `${controlId}-hint`;
  const errorId = `${controlId}-error`;

  const describedBy = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(" ");

  return (
    <div className={cx.field}>
      <label className={cx.fieldLabel} htmlFor={controlId}>
        {label}
        {required === true ? <span aria-hidden="true"> *</span> : null}
      </label>

      {children({
        controlId,
        describedBy: describedBy.length > 0 ? describedBy : undefined,
        invalid: error !== undefined,
      })}

      {hint !== undefined ? (
        <span className={cx.fieldHint} id={hintId}>
          {hint}
        </span>
      ) : null}

      {error !== undefined ? (
        // role="alert" so the error is announced when it appears, not only when the
        // user happens to navigate onto it.
        <span className={cx.fieldError} id={errorId} role="alert">
          {error}
        </span>
      ) : null}
    </div>
  );
}

export interface TextInputProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "id" | "aria-describedby"
> {
  readonly label: string;
  readonly hint?: string | undefined;
  readonly error?: string | undefined;
}

export function TextInput({ label, hint, error, required, ...rest }: TextInputProps) {
  return (
    <FieldShell label={label} hint={hint} error={error} required={required}>
      {({ controlId, describedBy, invalid }) => (
        <input
          {...rest}
          id={controlId}
          className={cx.fieldControl}
          required={required}
          aria-describedby={describedBy}
          aria-invalid={invalid ? true : undefined}
        />
      )}
    </FieldShell>
  );
}

export interface SelectOption {
  readonly value: string;
  /** Display text. Supplied by the caller from product-language — never hardcoded here. */
  readonly label: string;
}

export interface SelectProps extends Omit<
  SelectHTMLAttributes<HTMLSelectElement>,
  "id" | "aria-describedby" | "children"
> {
  readonly label: string;
  readonly options: readonly SelectOption[];
  readonly hint?: string | undefined;
  readonly error?: string | undefined;
}

export function Select({ label, options, hint, error, required, ...rest }: SelectProps) {
  return (
    <FieldShell label={label} hint={hint} error={error} required={required}>
      {({ controlId, describedBy, invalid }) => (
        <select
          {...rest}
          id={controlId}
          className={cx.fieldControl}
          required={required}
          aria-describedby={describedBy}
          aria-invalid={invalid ? true : undefined}
        >
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      )}
    </FieldShell>
  );
}

/* --------------------------------------------------------------- StateChip */

/** The five user-visible states, plus the "Past" display treatment over any of them. */
export type StateChipState = StateTokenKey | "past";

export interface StateChipProps {
  /** The state IDENTIFIER. Never a display string. */
  readonly state: StateChipState;
  /**
   * The words to show, supplied by the caller from @herefor/product-language.
   * This package deliberately cannot produce them.
   */
  readonly label: string;
  /**
   * Optional supporting detail, e.g. "1 of 2 people confirmed". Also caller-supplied:
   * coverage is a count, and the counting belongs to the domain, not to a chip.
   */
  readonly detail?: string;
}

/**
 * The state of a need, as a chip.
 *
 * Colour is never the only signal (plan 24.6): the chip always carries its label as
 * text and a shape marker, so meaning survives greyscale printing, colour blindness
 * and forced-colours modes. It is not interactive and takes no click handler — a
 * status is something you read, not something you press.
 */
export function StateChip({ state, label, detail }: StateChipProps) {
  return (
    <span className={[cx.chip, `${cx.chip}--${kebab(state)}`].join(" ")} data-state={state}>
      <span className={cx.chipDot} aria-hidden="true" />
      <span>
        <span>{label}</span>
        {detail === undefined ? null : (
          <>
            {" · "}
            <span>{detail}</span>
          </>
        )}
      </span>
    </span>
  );
}

/* --------------------------------------------------------------- Card etc. */

export interface CardProps {
  /** Optional heading text, caller-supplied. */
  readonly title?: string;
  /** Heading level, so a card never breaks the page's heading order. */
  readonly titleLevel?: 1 | 2 | 3 | 4 | 5 | 6;
  readonly children: ReactNode;
}

export function Card({ title, titleLevel = 3, children }: CardProps) {
  const Heading = `h${String(titleLevel)}` as "h1" | "h2" | "h3" | "h4" | "h5" | "h6";

  return (
    <section className={cx.card}>
      {title !== undefined ? <Heading className={cx.cardTitle}>{title}</Heading> : null}
      {children}
    </section>
  );
}

export type StatusTone = "info" | "success" | "warning" | "error";

export interface StatusMessageProps {
  readonly tone?: StatusTone;
  /** Caller-supplied message text. */
  readonly children: ReactNode;
  /**
   * Announce the message when it appears. `assertive` interrupts the user, so it is
   * reserved for errors — a polite region is the calm default this product wants.
   */
  readonly live?: "off" | "polite" | "assertive";
}

const statusToneClass: Record<StatusTone, string> = {
  info: cx.statusInfo,
  success: cx.statusSuccess,
  warning: cx.statusWarning,
  error: cx.statusError,
};

export function StatusMessage({ tone = "info", live = "polite", children }: StatusMessageProps) {
  return (
    <div
      className={[cx.status, statusToneClass[tone]].join(" ")}
      role={tone === "error" ? "alert" : "status"}
      aria-live={live === "off" ? undefined : live}
    >
      {children}
    </div>
  );
}

export interface EmptyStateProps {
  readonly title: string;
  readonly description?: string;
  readonly action?: ReactNode;
}

export function EmptyState({ title, description, action }: EmptyStateProps) {
  return (
    <div className={cx.emptyState}>
      <p>
        <strong>{title}</strong>
      </p>
      {description !== undefined ? <p>{description}</p> : null}
      {action}
    </div>
  );
}

export interface SkeletonProps {
  /**
   * What is loading, for assistive technology. Required, not optional: an
   * unannounced loading state is a silent pause for a screen-reader user.
   */
  readonly label: string;
  readonly lines?: number;
}

export function Skeleton({ label, lines = 3 }: SkeletonProps) {
  return (
    <div role="status" aria-live="polite" aria-busy="true">
      <span className={cx.visuallyHidden}>{label}</span>
      {Array.from({ length: lines }, (_, index) => (
        // Class, not an inline style: the height and gap are token-derived, and an
        // inline literal here would be exactly the raw value the kernel forbids.
        <div key={index} className={`${cx.skeleton} ${cx.skeletonLine}`} aria-hidden="true" />
      ))}
    </div>
  );
}
